import React, { useState } from "react";
import { useSelector } from "react-redux";
import { Button, CircularProgress, Box, Snackbar, Alert, Tooltip } from "@mui/material";
import { Layout } from "../../components/layout";
import PersonsInfo from "../../components/PersonsInfo";
import PageSection from "../../components/PageSection";
import {
  selectUser,
} from "../../store/selectors/authSelectors";
import { PlusIcon } from "../../components/Icons";
import {
  useModal,
  ApproveConfirmationModalContent,
  QRCodeSignModalContent,
} from "../../components/Modal";
import {
  TreatmentAppointmentsTableHeader,
  TreatmentPlanAppointmentRow,
  TreatmentPlanFooter,
  TreatmentPlanHeader,
  TreatmentPlanEditableSection,
  SummarizedView,
} from "./components";
import { useTreatmentPlanPage } from "./hooks";
import { useTreatmentPlanReApprove } from "./hooks/useTreatmentPlanReApprove";
import { treatmentPlanService } from "../../services/treatmentPlanService";
import {
  downloadTreatmentPlanPdf,
  generateTreatmentPlanPdfBase64,
} from "../../utils/downloadTreatmentPlanPdf";
import { downloadSummarizedTreatmentPlanPdf } from "../../utils/downloadSummarizedTreatmentPlanPdf";
import { generateSummarizedContent } from "../../utils/generateSummarizedContent";
import {
  buildTreatmentPlanMetaItems,
  getCanEditTreatmentPlanHeaderStatus,
  getTreatmentPlanHeaderStatusOptions,
  getTreatmentPlanPatientName,
  getTreatmentPlanProviderName,
  getTreatmentPlanStatusLabel,
  getTreatmentPlanStatusType,
} from "../../utils/treatmentPlanDisplayUtils";
import {
  RejectionCommentBox,
  RejectionCommentTitle,
  RejectionCommentText,
  ViewToggleContainer,
  ViewToggleButton,
} from "./TreatmentPlanPage.styled";
import { colors } from "../../theme/colors";
import { UI } from "../../constants";

type ActiveView = "detailed" | "summarized";

const TreatmentPlanPage: React.FC = () => {
  const { openModal, closeModal, updateModal } = useModal();
  const { openReApproveConfirmation } = useTreatmentPlanReApprove();
  const user = useSelector(selectUser);

  const {
    id,
    t,
    isProvider,
    canEditTreatmentPlan,
    notification,
    showNotification,
    hideNotification,
    treatmentPlan,
    loading,
    error,
    fetchAndSyncPlan,
    handleSaveHeaderDetails,
    handleSaveTreatmentSummary,
    handleSaveProviderNote,
    handleSavePatientSummary,
    sortedAppointments,
    hasOptions,
    editingAppointmentId,
    savingAppointmentId,
    deletingAppointmentId,
    draggingAppointmentIndex,
    dragOverAppointmentIndex,
    isAppointmentInteractionBlocked,
    handleStartAppointmentEdit,
    handleCancelAppointmentEdit,
    handleDraftAppointmentChange,
    handleDraftProcedureChange,
    handleSaveAppointmentEdit,
    handleDeleteAppointment,
    handleAddAppointment,
    handleDraftCdtChange,
    handleAppointmentDragStart,
    handleAppointmentDragOver,
    handleAppointmentDragLeave,
    handleAppointmentDragEnd,
    handleAppointmentDrop,
    getAppointmentDraft,
    getAppointmentHasChanges,
  } = useTreatmentPlanPage();

  const [activeView, setActiveView] = useState<ActiveView>("detailed");
  const [isSummarizedEditing, setIsSummarizedEditing] = useState(false);
  const [isHeaderEditing, setIsHeaderEditing] = useState(false);
  const [isTreatmentSummaryEditing, setIsTreatmentSummaryEditing] =
    useState(false);
  const [isProviderNoteEditing, setIsProviderNoteEditing] = useState(false);

  const showSummarizedTab = treatmentPlan ? treatmentPlan.status >= 3 : false;

  const isFooterActionsDisabled =
    isAppointmentInteractionBlocked ||
    isSummarizedEditing ||
    isHeaderEditing ||
    isTreatmentSummaryEditing ||
    isProviderNoteEditing;

  const isDetailedViewEditing =
    isHeaderEditing ||
    isTreatmentSummaryEditing ||
    isProviderNoteEditing ||
    isAppointmentInteractionBlocked;

  const handleDownloadDetailedPdf = async () => {
    if (!treatmentPlan) return;
    try {
      await downloadTreatmentPlanPdf({ treatmentPlan });
    } catch (err) {
      console.error("Error downloading PDF:", err);
    }
  };

  const handleDownloadSummarizedPdf = async () => {
    if (!treatmentPlan) return;
    const content =
      treatmentPlan.patientSummary?.trim() ||
      generateSummarizedContent(treatmentPlan);
    try {
      await downloadSummarizedTreatmentPlanPdf({ treatmentPlan, content });
    } catch (err) {
      console.error("Error downloading summarized PDF:", err);
    }
  };

  const handleEmailForReview = async () => {
    if (!id) return;
    try {
      await treatmentPlanService.shareByEmail(id);
      showNotification(t("treatmentPlan.emailSentSuccess"));
    } catch (err) {
      console.error("Error sending email for review:", err);
      showNotification(t("treatmentPlan.emailSendFailed"), "error");
    }
  };

  const handleOpenToSign = () => {
    if (!id) return;
    openModal({
      id: "qr-code-sign",
      content: <QRCodeSignModalContent treatmentPlanId={id} />,
      showCloseButton: true,
    });
  };

  const handleReApproveClick = () => {
    if (!user?.id || !id || !treatmentPlan) return;

    openReApproveConfirmation(async () => {
      try {
        const { pdfFileBase64 } = await generateTreatmentPlanPdfBase64({
          treatmentPlan,
        });
        await treatmentPlanService.approve(id, user.id, pdfFileBase64);
        await fetchAndSyncPlan(true);
        showNotification(t("treatmentPlan.changesSaved"));
      } catch (err) {
        console.error("Error re-approving treatment plan:", err);
        showNotification(t("editTreatmentPlan.failedToSave"), "error");
        throw err;
      }
    });
  };

  const handleApprove = () => {
    if (!user?.id || !id || !treatmentPlan) return;
    openModal({
      id: "approve-confirmation-modal",
      title: "",
      content: (
        <ApproveConfirmationModalContent
          onCancel={() => closeModal("approve-confirmation-modal")}
          onApprove={async () => {
            try {
              const { pdfFileBase64 } = await generateTreatmentPlanPdfBase64({
                treatmentPlan,
              });
              await treatmentPlanService.approve(id, user.id, pdfFileBase64);
              closeModal("approve-confirmation-modal");
              await fetchAndSyncPlan(true);
              showNotification(t("treatmentPlan.approvedSuccess"));
            } catch (err) {
              console.error("Error approving treatment plan:", err);
              showNotification(t("treatmentPlan.approveFailed"), "error");
            }
          }}
          onLoadingChange={(loading) =>
            updateModal("approve-confirmation-modal", {
              showCloseButton: !loading,
              closeOnBackdropClick: !loading,
            })
          }
        />
      ),
      showCloseButton: true,
      closeOnBackdropClick: true,
      maxWidth: "600px",
      containerPadding: "60px 90px",
      contentPadding: "0",
    });
  };

  if (loading) {
    return (
      <Layout>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "400px",
          }}
        >
          <CircularProgress size={24} />
        </Box>
      </Layout>
    );
  }

  if (error || !treatmentPlan) {
    return (
      <Layout>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "400px",
            gap: 2,
          }}
        >
          <div>{error || t("treatmentPlan.notFound")}</div>
          <Button onClick={() => void fetchAndSyncPlan(true)}>
            {t("treatmentPlan.retry")}
          </Button>
        </Box>
      </Layout>
    );
  }

  const metaItems = buildTreatmentPlanMetaItems(treatmentPlan, t);

  const persons = [
    {
      name: getTreatmentPlanPatientName(treatmentPlan),
      role: t("treatmentPlan.patient"),
    },
    {
      name: getTreatmentPlanProviderName(treatmentPlan),
      role: t("treatmentPlan.initiatedBy"),
      avatarUrl: treatmentPlan.providerAvatarUrl || undefined,
    },
  ];

  const summarizedContent =
    treatmentPlan.patientSummary?.trim() ||
    generateSummarizedContent(treatmentPlan);

  const canEditHeaderStatus = getCanEditTreatmentPlanHeaderStatus(
    treatmentPlan.status,
  );
  const headerStatusOptions = getTreatmentPlanHeaderStatusOptions(
    treatmentPlan.status,
    t,
  );

  const getStatusLabel = (status: number) =>
    getTreatmentPlanStatusLabel(status, t);
  const getStatusType = (status: number) =>
    getTreatmentPlanStatusType(status);

  const viewToggle =
    showSummarizedTab ? (
      <ViewToggleContainer>
        <ViewToggleButton
          isActive={activeView === "detailed"}
          onClick={() => {
            if (isSummarizedEditing) return;
            setActiveView("detailed");
            setIsSummarizedEditing(false);
          }}
          disabled={isSummarizedEditing}
          disableRipple={activeView === "detailed"}
        >
          {t("treatmentPlan.detailedView")}
        </ViewToggleButton>
        <ViewToggleButton
          isActive={activeView === "summarized"}
          onClick={() => {
            if (isDetailedViewEditing) return;
            setActiveView("summarized");
          }}
          disabled={isDetailedViewEditing}
          disableRipple={activeView === "summarized"}
        >
          {t("treatmentPlan.summarizedView")}
        </ViewToggleButton>
      </ViewToggleContainer>
    ) : undefined;

  const headerActions = (
    <PersonsInfo persons={persons} variant="treatmentPlan" margin="0" />
  );

  return (
    <Layout topBarActions={viewToggle}>
      <TreatmentPlanHeader
        title={treatmentPlan.name}
        status={treatmentPlan.status}
        metaItems={metaItems}
        actions={headerActions}
        isEditable={canEditTreatmentPlan}
        canEditStatus={canEditHeaderStatus}
        statusOptions={headerStatusOptions}
        getStatusLabel={getStatusLabel}
        getStatusType={getStatusType}
        onSave={handleSaveHeaderDetails}
        onEditingChange={setIsHeaderEditing}
      />

      {treatmentPlan.status === 4 && treatmentPlan.rejectionComment && (
        <RejectionCommentBox>
          <RejectionCommentTitle>
            {t("treatmentPlan.commentFromPatient")}
          </RejectionCommentTitle>
          <RejectionCommentText>
            {treatmentPlan.rejectionComment}
          </RejectionCommentText>
        </RejectionCommentBox>
      )}

      {activeView === "detailed" && (
        <>
          <TreatmentPlanEditableSection
            title={t("treatmentPlan.treatmentSummary")}
            content={treatmentPlan.treatmentSummary}
            isEditable={canEditTreatmentPlan}
            editorType="richText"
            placeholder={t("editTreatmentPlan.treatmentSummaryPlaceholder")}
            allowHtml
            onSave={handleSaveTreatmentSummary}
            onEditingChange={setIsTreatmentSummaryEditing}
          />

          {(treatmentPlan.providerNote || canEditTreatmentPlan) && (
            <TreatmentPlanEditableSection
              title={t("treatmentPlan.notesRegardingTreatment")}
              content={treatmentPlan.providerNote || ""}
              isEditable={canEditTreatmentPlan}
              editorType="multilineText"
              placeholder={t("editTreatmentPlan.noteFromProviderPlaceholder")}
              onSave={handleSaveProviderNote}
              onEditingChange={setIsProviderNoteEditing}
            />
          )}

          <PageSection title={t("treatmentPlan.treatmentSteps")}>
            <TreatmentAppointmentsTableHeader
              showActionsColumn={canEditTreatmentPlan}
            />
            {sortedAppointments.map((appointment, index) => {
              const isLastAppointment = index === sortedAppointments.length - 1;

              return (
                <TreatmentPlanAppointmentRow
                  key={appointment.id}
                  appointment={appointment}
                  index={index}
                  isLastAppointment={isLastAppointment}
                  draft={getAppointmentDraft(appointment)}
                  hasChanges={getAppointmentHasChanges(
                    appointment,
                    isLastAppointment,
                  )}
                  isEditing={editingAppointmentId === appointment.id}
                  isSaving={savingAppointmentId === appointment.id}
                  isDeleting={deletingAppointmentId === appointment.id}
                  isDraggable={
                    canEditTreatmentPlan && !isAppointmentInteractionBlocked
                  }
                  isDragging={draggingAppointmentIndex === index}
                  isDragOver={dragOverAppointmentIndex === index}
                  onDragOver={(event) =>
                    handleAppointmentDragOver(event, index)
                  }
                  onDragLeave={handleAppointmentDragLeave}
                  onDrop={(event) => handleAppointmentDrop(event, index)}
                  onDraftAppointmentChange={(field, value) =>
                    handleDraftAppointmentChange(appointment.id, field, value)
                  }
                  onDraftProcedureChange={(procedureIndex, field, value) =>
                    handleDraftProcedureChange(
                      appointment.id,
                      procedureIndex,
                      field,
                      value,
                    )
                  }
                  onDraftCdtChange={(
                    procedureIndex,
                    value,
                    allCdtCodes,
                    allDescriptions,
                    totalFee,
                    totalDurationMinutes,
                  ) =>
                    handleDraftCdtChange(
                      appointment.id,
                      procedureIndex,
                      value,
                      allCdtCodes,
                      allDescriptions,
                      totalFee,
                      totalDurationMinutes,
                    )
                  }
                  onSave={(durationMinutes) =>
                    handleSaveAppointmentEdit(appointment.id, durationMinutes)
                  }
                  onCancel={() => handleCancelAppointmentEdit(appointment.id)}
                  onEdit={
                    canEditTreatmentPlan &&
                    !(
                      isAppointmentInteractionBlocked &&
                      editingAppointmentId !== appointment.id
                    )
                      ? () => handleStartAppointmentEdit(appointment)
                      : undefined
                  }
                  onDelete={
                    canEditTreatmentPlan &&
                    !(
                      isAppointmentInteractionBlocked &&
                      editingAppointmentId !== appointment.id
                    )
                      ? () => {
                          void handleDeleteAppointment(appointment.id);
                        }
                      : undefined
                  }
                  onDragStart={(event) =>
                    handleAppointmentDragStart(event, index)
                  }
                  onDragEnd={handleAppointmentDragEnd}
                  showActionsColumn={canEditTreatmentPlan}
                />
              );
            })}
            {canEditTreatmentPlan && (
            <Box sx={{ mt: "24px" }}>
              {isAppointmentInteractionBlocked ? (
                <Tooltip
                  title={t("treatmentPlan.finishEditingAppointmentFirst")}
                  arrow
                  placement="top"
                >
                  <span>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={
                        <PlusIcon
                          width="20px"
                          height="20px"
                          fill={colors.text.secondary}
                        />
                      }
                      onClick={handleAddAppointment}
                      disabled
                    >
                      {t("editTreatmentPlan.addAppointment")}
                    </Button>
                  </span>
                </Tooltip>
              ) : (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={
                    <PlusIcon
                      width="20px"
                      height="20px"
                      fill={colors.primary.main}
                    />
                  }
                  onClick={handleAddAppointment}
                >
                  {t("editTreatmentPlan.addAppointment")}
                </Button>
              )}
            </Box>
            )}
          </PageSection>
        </>
      )}

      {activeView === "summarized" && (
        <Box sx={{ mt: "24px" }}>
          <SummarizedView
            content={summarizedContent}
            canEdit={
              isProvider && treatmentPlan.status !== 7
            }
            onEditingChange={setIsSummarizedEditing}
            onSave={handleSavePatientSummary}
          />
        </Box>
      )}

      <TreatmentPlanFooter
        totalFee={treatmentPlan.totalFee}
        status={treatmentPlan.status}
        isProvider={isProvider}
        hasMultipleOptions={hasOptions}
        isSummarizedView={activeView === "summarized"}
        canBeReapproved={treatmentPlan.canBeReapproved}
        onApprove={handleApprove}
        onReApprove={handleReApproveClick}
        onDownloadPdf={handleDownloadDetailedPdf}
        onDownloadSummarizedPdf={handleDownloadSummarizedPdf}
        onEmailForReview={handleEmailForReview}
        onOpenToSign={handleOpenToSign}
        actionsDisabled={isFooterActionsDisabled}
        actionsDisabledReason={t("treatmentPlan.footerActionsBlockedWhileEditing")}
      />

      <Snackbar
        open={notification.open}
        autoHideDuration={UI.SNACKBAR_DURATION}
        onClose={hideNotification}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={hideNotification}
          severity={notification.severity}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Layout>
  );
};

export default TreatmentPlanPage;
